// src/context/MessagingContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { getSessionUser } from "../utils/auth/sessionUser";
import { isAppSession, useAuthSession } from "../lib/http/session";
import { isPatientSession } from "../utils/userPrefs/validatePermissions";
import CommunicationsService, { ConnectionState, CommunicationNotification } from "../services/CommunicationsService";
import { getConversations, getQueueConversations } from "../api/messaging/ConversationsApi";
import {
	Conversation,
	ParticipantAddedEvent,
	ParticipantRemovedEvent,
	ConversationAssignedEvent,
	ConversationQueuedEvent,
} from "../types/messaging";
import type { UserUuid } from "../types/identity";

// MessagingConnectionState alias kept for consumers of this context
export type MessagingConnectionState = ConnectionState;

export type MessagingPanelTab = "my" | "queue";

export interface OpenPanelOptions {
	tab?: MessagingPanelTab;
	highlightConversationId?: string;
}

interface MessagingContextType {
	// Panel toggle
	isPanelOpen: boolean;
	openPanel: (options?: OpenPanelOptions) => void;
	closePanel: () => void;
	togglePanel: () => void;

	// Conversation list tab + the queue row a click-through asked to highlight
	panelTab: MessagingPanelTab;
	setPanelTab: (tab: MessagingPanelTab) => void;
	highlightConversationId: string | null;
	clearHighlight: () => void;

	// Background connection state (always active)
	connectionState: MessagingConnectionState;

	// Conversations & counts — maintained in background regardless of panel state
	conversations: Conversation[];
	queueConversations: Conversation[];
	totalUnreadCount: number;
	queueCount: number;

	// Called by useMessaging when the user reads a conversation so the badge decrements
	markConversationAsRead: (conversationId: string) => void;

	// Allows useMessaging to trigger a refresh
	refreshConversations: () => void;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

export const useMessagingPanel = (): MessagingContextType => {
	const context = useContext(MessagingContext);
	if (!context) {
		throw new Error("useMessagingPanel must be used within a MessagingProvider");
	}
	return context;
};

// Non-throwing variant for providers that may render outside a MessagingProvider.
export const useOptionalMessagingPanel = (): MessagingContextType | undefined => useContext(MessagingContext);

interface MessagingProviderProps {
	children: ReactNode;
}

export const MessagingProvider: React.FC<MessagingProviderProps> = ({ children }) => {
	const [isPanelOpen, setIsPanelOpen] = useState(false);
	const [panelTab, setPanelTab] = useState<MessagingPanelTab>("my");
	const [highlightConversationId, setHighlightConversationId] = useState<string | null>(null);
	const [conversations, setConversations] = useState<Conversation[]>([]);
	const [queueConversations, setQueueConversations] = useState<Conversation[]>([]);
	const [connectionState, setConnectionState] = useState<MessagingConnectionState>("disconnected");

	// User identity resolved from the session
	const [userId, setUserId] = useState<UserUuid | null>(null);

	// A confined session reaches only identity, so it opens no socket and loads no conversations.
	const isLoggedIn = isAppSession(useAuthSession());

	// Derived counts
	const totalUnreadCount = conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0);
	const queueCount =
		isPatientSession()
			? 0
			: queueConversations.filter((c) => c.assignment?.status !== "assigned").length;

	// ─── Resolve user identity on mount ───────────────────────────────────────
	useEffect(() => {
		if (!isLoggedIn) return;
		const init = async () => {
			try {
				const userData = getSessionUser();
				if (!userData.id) return;
				setUserId(userData.id);
			} catch (e) {
				console.error("MessagingProvider: failed to resolve user identity", e);
			}
		};
		init();
	}, [isLoggedIn]);

	// ─── Data loading helpers ─────────────────────────────────────────────────
	const loadConversations = useCallback(async () => {
		if (!userId || !isLoggedIn) return;
		try {
			const data = await getConversations({ user_id: userId });
			setConversations(data);
			// Note: Do NOT eagerly subscribe to all conversations here. Only subscribe when
			// a user opens a specific conversation to avoid accumulating pending connections.
			// MessagingPanel handles subscription when a conversation is selected.
		} catch (e) {
			console.error("MessagingProvider: error loading conversations", e);
		}
	}, [userId, isLoggedIn]);

	const loadQueueConversations = useCallback(async () => {
		if (!isLoggedIn || isPatientSession()) return;
		try {
			const data = await getQueueConversations();
			setQueueConversations(data);
		} catch (e) {
			console.error("MessagingProvider: error loading queue", e);
		}
	}, [isLoggedIn]);

	// ─── Background WebSocket event handlers ─────────────────────────────────
	useEffect(() => {
		if (!userId || !isLoggedIn) return;

		CommunicationsService.registerListeners("messaging-background", {
			onAddedToConversation: (event: ParticipantAddedEvent) => {
				CommunicationsService.subscribeToConversation(event.conversation_id);
				loadConversations();
			},
			onRemovedFromConversation: (event: ParticipantRemovedEvent) => {
				CommunicationsService.unsubscribeFromConversation(event.conversation_id);
				setConversations((prev) => prev.filter((c) => c.id !== event.conversation_id));
			},
			onConversationAssigned: (_event: ConversationAssignedEvent) => {
				loadConversations();
				loadQueueConversations();
			},
			onQueueConversationAdded: (_event: ConversationQueuedEvent) => {
				loadQueueConversations();
				loadConversations();
			},
			onConnectionStateChanged: (state: ConnectionState) => {
				setConnectionState(state);
				if (state === "connected") {
					loadConversations();
					loadQueueConversations();
				}
			},
		});

		return () => {
			CommunicationsService.unregisterListeners("messaging-background");
		};
	}, [userId, isLoggedIn, loadConversations, loadQueueConversations]);

	// ─── Notification-driven badge updates ───────────────────────────────────
	// This is the ONLY place the unread badge is incremented.
	// onMessageSent is intentionally NOT used for badge updates — it would cause
	// double-increments when the conversation channel is subscribed.
	useEffect(() => {
		if (!userId || !isLoggedIn) return;

		CommunicationsService.registerListeners("messaging-badge", {
			onNotificationReceived: (notification: CommunicationNotification) => {
				// Only message notifications carry a conversation id in related_entity_id
				if (notification.type !== "info" || !notification.auto_delete || !notification.related_entity_id) {
					return;
				}
				setConversations((prev) => {
					const exists = prev.some((c) => c.id === notification.related_entity_id);
					if (!exists) {
						// Conversation not in state yet (e.g. first message in a new conversation).
						// Trigger a reload outside the setState call.
						setTimeout(() => loadConversations(), 0);
						return prev;
					}
					return prev.map((c) =>
						c.id === notification.related_entity_id
							? { ...c, unread_count: (c.unread_count || 0) + 1, last_message_at: notification.created_at }
							: c
					);
				});
			},
		});

		return () => {
			CommunicationsService.unregisterListeners("messaging-badge");
		};
	}, [userId, isLoggedIn, loadConversations]);

	// ─── Connect WebSocket as soon as we have the user identity ──────────────
	useEffect(() => {
		if (!isLoggedIn) {
			// Actual logout — tear down the shared connection.
			CommunicationsService.disconnect();
			setConversations([]);
			setConnectionState("disconnected");
			return;
		}

		if (!userId) {
			// Identity is still resolving asynchronously. Do NOT disconnect —
			// CommunicationsService is a shared singleton and NotificationContext
			// may already have an active connection. Disconnecting here would kill
			// it and cause a reconnect cycle that leaves conversations empty.
			setConnectionState(CommunicationsService.getConnectionState());
			return;
		}

		setConnectionState(CommunicationsService.getConnectionState());
		// connect() is idempotent — when NotificationContext already established
		// the socket, this is a safe no-op.
		CommunicationsService.connect(userId);

		// NotificationContext may have already connected before MessagingContext
		// registered its onConnectionStateChanged listener, so the 'connected'
		// event is never received here. Eagerly load so the badge is populated
		// regardless of connection-event ordering.
		loadConversations();
		loadQueueConversations();
	}, [isLoggedIn, userId, loadConversations, loadQueueConversations]);

	// ─── Panel open/close ─────────────────────────────────────────────────────
	// A plain open (the TopBar button) always lands on My Conversations.
	const openPanel = useCallback((options?: OpenPanelOptions) => {
		setPanelTab(options?.tab ?? "my");
		setHighlightConversationId(options?.highlightConversationId ?? null);
		setIsPanelOpen(true);
	}, []);

	const closePanel = useCallback(() => {
		setIsPanelOpen(false);
		setHighlightConversationId(null);
	}, []);

	const clearHighlight = useCallback(() => setHighlightConversationId(null), []);

	// ─── Exposed action: mark a conversation as read in context state ─────────
	const markConversationAsRead = useCallback((conversationId: string) => {
		setConversations((prev) =>
			prev.map((c) => (c.id === conversationId ? { ...c, unread_count: 0 } : c))
		);
	}, []);

	return (
		<MessagingContext.Provider
			value={{
				isPanelOpen,
				openPanel,
				closePanel,
				togglePanel: () => (isPanelOpen ? closePanel() : openPanel()),
				panelTab,
				setPanelTab,
				highlightConversationId,
				clearHighlight,
				connectionState,
				conversations,
				queueConversations,
				totalUnreadCount,
				queueCount,
				markConversationAsRead,
				refreshConversations: loadConversations,
			}}
		>
			{children}
		</MessagingContext.Provider>
	);
};
